import React, { useEffect, useState, useContext } from "react";
import { useLocation } from "react-router-dom";

// MUI components
import { Box, Tooltip, Typography } from "@mui/material";

// MUI icons
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import { ReactComponent as ExportButton } from "../../../images/exportButton.svg";

// Icons from project
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";

// Components and functions from project
import CommonAccordion from "../../custom-components/CommonAccordion";
import CommonTable from "../../custom-components/CommonTestCasesTable";
import { flattenArray, flattenObject } from "../../../utils/dataFlatteners";
import CommonDownload from "../../custom-components/CommonDownload";
import CommonCopy from "../../custom-components/CommonCopy";
import { TestCaseFlowDataContext } from "../../main-pages/TestcaseGenerationPage";
import ALMConnectionPage from "../../main-pages/input-pages/ALMConnection/ALMConnectionPage";

const TestCasesComponent = ({ testCasesData, traceabilityMatrix }) => {
  const location = useLocation();
  const userStory = location.state.userStory || {};
  const context = useContext(TestCaseFlowDataContext);
  const functionalSpecDocument = context?.functionalSpecDocument || {}; // Prevents destructuring error
  const [testCases, setTestCases] = useState(testCasesData);
  const [flattenedFSD, setFlattenedFSD] = useState({});
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadRTMOpen, setDownloadRTMOpen] = useState(false);
  const multipleTestCases = location.state?.multipleTestCases || [];
  const [openProjectPage, setOpenProjectPage] = useState(false);

  const [activeThumb, setActiveThumb] = useState(null);

  const defaultTestCasesExpanded = Array.isArray(testCases)
    ? testCases.length > 0
    : true;

  const handleDownloadClick = () => {
    setDownloadOpen(true);
  };
  const handleRTMDownloadClick = () => {
    setDownloadRTMOpen(true);
  };

  const exportTestCasesToqTest = async () => {
    setOpenProjectPage(true);
  };

  const handleExportClick = () => {
    const formatted = formattedTestCases.map((test) => ({
      title: test["Test Case Title"] || test["Title"],
      custom_preconds: test["Pre-conditions"] || "",
      custom_steps: test["Test Steps"] || "",
      custom_expected: test["Expected Result"] || "",
    }));

    setOpenProjectPage({
      open: true,
      testCases: formatted,
    });
  };

  const allTestCasesReady =
    multipleTestCases.length > 0 &&
    multipleTestCases.every((tc) => tc["Test Case ID"]);

  const formattedTestCases = [];

  testCases.map((testCase) => {
    const flattenTestCase = {};
    Object.entries(testCase).map(([key, value]) => {
      if (Array.isArray(value)) {
        return (flattenTestCase[key] = flattenArray(value));
      } else if (typeof value === "object" && !!value) {
        return (flattenTestCase[key] = flattenObject(value));
      } else {
        return (flattenTestCase[key] = value);
      }
    });
    return formattedTestCases.push(flattenTestCase);
  });

  const formattedTestCasesObject = [formattedTestCases];

  const handleThumbClick = (thumbType) => {
    setActiveThumb((prev) => (prev === thumbType ? null : thumbType));
  };

  const tableDataConverter = (testCases) => {
    const headers = Object.keys(testCases[0]);
    const values = testCases.map((eachTestCase) => {
      return Object.values(eachTestCase);
    });
    return { headers, values };
  };

  const handleTestConditionsTableData = (testConditionsData) => {
    const headers = ["Requirement", "Test conditions"];
    const values = Object.entries(testConditionsData).map(([key, value]) => [
      key,
      flattenArray(value),
    ]);
    return { headers, values };
  };

  const handleTracabilityMatrixData = (traceabilityMatrixData) => {
    const headers = [
      "Requirement ID",
      "User Story Reference",
      "Risk Priority",
      "Test Conditions Summary",
      "Test Case ID(s)",
    ];

    const values = [];
    Object.entries(traceabilityMatrixData).map(([key, value]) => {
      if (key === "Summary of Findings") return;
      if (Array.isArray(value)) {
        value.map((eachTrace) => {
          const requirementId = eachTrace["Requirement ID"] || "";
          const userStoryId = eachTrace["User Story Reference"] || "";
          const riskPriority = eachTrace["Risk Priority"] || "";
          const testConditionId = eachTrace["Test Conditions Summary"] || "";
          const testCaseIds = (eachTrace["Test Case ID(s)"] || [])
            .map((testCase) => testCase["Test Case ID"] || "")
            .join(", ");
          return values.push([
            requirementId,
            userStoryId,
            riskPriority,
            testConditionId,
            testCaseIds,
          ]);
        });
      } else {
        console.error(`Expected an array but got: ${typeof value}`, value);
      }
    });

    return { headers, values };
  };

  const handleSummaryOfFindings = (traceabilityMatrixData) => {
    const summaryOfFindings =
      traceabilityMatrixData["Summary of Findings"] || {};
    const uncoveredRequirements =
      summaryOfFindings["Uncovered Requirements"] || [];
    const redundantTestCases = summaryOfFindings["Redundant Test Cases"] || [];
    const summaryData = [];
    if (uncoveredRequirements.length > 0) {
      summaryData.push(
        `\nUNCOVERED REQUIREMENTS:\n\n${uncoveredRequirements
          .map((eachRequirement) => {
            return `Requirement ID: ${eachRequirement["Requirement ID"]}\nDescription: ${eachRequirement["Description"]}\n`;
          })
          .join("\n")}`
      );
    }
    if (redundantTestCases.length > 0) {
      summaryData.push(
        `\nREDUNDANT TEST CASES:\n${redundantTestCases
          .map((eachTestCase) => {
            return `Test Case ID: ${eachTestCase["Test Case ID"]}\nDescription: ${eachTestCase["Description"]}`;
          })
          .join("\n")}`
      );
    }

    return summaryData.join("\n");
  };

  useEffect(() => {
    if (Object.keys(functionalSpecDocument).length > 0) {
      const tempFlattenedFSD = {};
      Object.entries(functionalSpecDocument).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          tempFlattenedFSD[key] = value
            .map((item) =>
              typeof item === "object"
                ? Object.entries(item)
                    .map(([subKey, subValue]) => `${subKey}: ${subValue}`)
                    .join("\n")
                : item
            )
            .join("\n");
        } else if (typeof value === "object" && value !== null) {
          tempFlattenedFSD[key] = Object.entries(value)
            .map(([subKey, subValue]) => `${subKey}: ${subValue}`)
            .join("\n");
        } else {
          tempFlattenedFSD[key] = value.toString();
        }
      });

      setFlattenedFSD(tempFlattenedFSD);
    } else {
      console.warn("FSD is empty! Check context provider.");
    }
  }, [functionalSpecDocument]);

  const formatValueForDoc = (value) => {
    if (Array.isArray(value)) {
      return value
        .map((item) =>
          typeof item === "object"
            ? Object.entries(item)
                .map(([subKey, subValue]) => `${subKey}: ${subValue}`)
                .join("\n") + "\n"
            : item
        )
        .join("\n");
    } else if (typeof value === "object" && value !== null) {
      return (
        Object.entries(value)
          .map(([subKey, subValue]) => `${subKey}: ${subValue}`)
          .join("\n") + "\n"
      );
    } else if (typeof value === "string") {
      return value;
    }
    return value;
  };
  const functionalTestCasesCount = formattedTestCases.filter((test) =>
    test["Test Case ID"].startsWith("TCD_FT")
  ).length;
  const nonFunctionalTestCasesCount = formattedTestCases.filter((test) =>
    test["Test Case ID"].startsWith("TCD_NFT")
  ).length;
  const totalTestCasesCount =
    functionalTestCasesCount + nonFunctionalTestCasesCount;
  const testCasesTable = (
    <Box sx={{ height: "100%" }}>
      <Box
        sx={{
          maxHeight: "60vh",
          height: "100%",
        }}
      >
        <CommonTable
          {...tableDataConverter(formattedTestCases)}
          edittable={true}
          tableData={formattedTestCases}
          setTableData={setTestCases}
        />
      </Box>

      <Box display="flex" gap={4} alignItems="center">
        <Typography sx={{ fontSize: 14, mt: 2 }} variant="body1">
          Functional test cases: {functionalTestCasesCount}
        </Typography>
        <Typography sx={{ fontSize: 14, mt: 2 }} variant="body1">
          Non Functional test cases: {nonFunctionalTestCasesCount}
        </Typography>
        <Typography sx={{ fontSize: 14, mt: 2 }} variant="body1">
          Total test cases: {totalTestCasesCount}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          marginTop: 4,
          marginBottom: 2,
        }}
      >
        {/* Thumbs Up Button with Tooltip */}
        <Tooltip title="Like" arrow>
          <Box
            onClick={() => handleThumbClick("like")}
            sx={{
              cursor: "pointer",
              marginRight: 2,
              "&:hover": {
                color: "#49A3FF",
              },
            }}
          >
            {activeThumb === "like" ? (
              <ThumbUpIcon sx={{ fontSize: "24px" }} />
            ) : (
              <ThumbUpOutlinedIcon sx={{ fontSize: "24px" }} />
            )}
          </Box>
        </Tooltip>

        {/* Thumbs Down Button with Tooltip */}
        <Tooltip title="Dislike" arrow>
          <Box
            onClick={() => handleThumbClick("dislike")}
            sx={{
              cursor: "pointer",
              marginRight: 2,
              "&:hover": {
                color: "#FF4842",
              },
            }}
          >
            {activeThumb === "dislike" ? (
              <ThumbDownIcon sx={{ fontSize: "24px" }} />
            ) : (
              <ThumbDownOutlinedIcon sx={{ fontSize: "24px" }} />
            )}
          </Box>
        </Tooltip>
        <CommonCopy contentToCopy={formattedTestCases} />
      </Box>
    </Box>
  );

  return (
    <Box>
      <CommonAccordion
        summary={"Generated Test Cases"}
        details={testCasesTable}
        type={"output"}
        buttons={[
          {
            label: "Download RTM",
            onClick: handleRTMDownloadClick,
            variant: "text",
            startIcon: <FileDownloadOutlinedIcon />,
            sx: { textTransform: "none" },
            disabled:
              !traceabilityMatrix ||
              Object.keys(traceabilityMatrix).length === 0,
          },
          {
            label: "Export",
            onClick: handleExportClick,
            variant: "text",
            startIcon: (
              <ExportButton
                style={{
                  opacity: allTestCasesReady ? 1 : 0.5,
                  pointerEvents: allTestCasesReady ? "auto" : "none",
                  cursor: allTestCasesReady ? "pointer" : "not-allowed",
                  color: !allTestCasesReady ? "grey" : "#49A3FF",
                }}
              />
            ),
            sx: { textTransform: "none" },
            disabled: !allTestCasesReady,
          },
          {
            label: "Download",
            onClick: handleDownloadClick,
            variant: "contained",
            startIcon: <GenerateButton />,
            sx: { float: "right", textTransform: "none" },
            disabled: !allTestCasesReady,
          },
        ]}
        fullScreenMode={true}
        defaultExpanded={defaultTestCasesExpanded}
      />

      {openProjectPage.open && (
        <ALMConnectionPage
          issueType={"Test Cases"}
          setPageCloser={setOpenProjectPage}
          testCasesData={testCasesData}
          exportData={formattedTestCases}
          taskType="Export"
        />
      )}

      <CommonDownload
        open={downloadOpen}
        onClose={() => setDownloadOpen(false)}
        serviceResponse={[formattedTestCases]}
        serviceName="TestCases_Report"
        wordData={[
          {
            heading: "User Story",
            type: "text",
            data:
              typeof userStory === "string" ? userStory : userStory["Title"],
          },
          {
            heading: "Test Cases",
            type: "table",
            data: { ...tableDataConverter(formattedTestCases) },
          },
        ]}
        documents={["xls", "csv"]}
      />

      <CommonDownload
        open={downloadRTMOpen}
        onClose={() => setDownloadRTMOpen(false)}
        serviceResponse={[
          {
            heading: "Requirement Traceability Matrix",
            type: "table",
            data: handleTracabilityMatrixData(traceabilityMatrix),
          },
          {
            heading: "Summary of Findings",
            type: "text",
            data: handleSummaryOfFindings(traceabilityMatrix),
          },
        ]}
        serviceName="Requirement Traceability Matrix"
        wordData={[
          {
            heading: "Requirement Traceability Matrix",
            type: "table",
            data: handleTracabilityMatrixData(traceabilityMatrix),
          },
          {
            heading: "Summary of Findings",
            type: "text",
            data: handleSummaryOfFindings(traceabilityMatrix),
          },
        ]}
        documents={["docx", "pdf"] || []}
      />
      {/* {openProjectPage && (
          <ALMConnectionPage
            issueType={"Test Cases"}
            setPageCloser={setOpenProjectPage}
            exportData={formattedTestCases}
            taskType="Export"
          />
        )} */}
    </Box>
  );
};

export default TestCasesComponent;